'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useState, useEffect } from 'react'
import Modal from '@/components/Modal'
import EmailConfirmationModal from '@/components/EmailConfirmationModal'
import LoginForm from '@/components/auth/LoginForm'
import RegisterForm from '@/components/auth/RegisterForm'

export default function LoginPage() {
  const [tab, setTab] = useState<'login' | 'register'>('login')
  
  // Estados para modal de confirmação
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [confirmationEmail, setConfirmationEmail] = useState('')
  
  // Estados para reenvio de email
  const [resendLoading, setResendLoading] = useState(false)
  const [resendError, setResendError] = useState<string | null>(null)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalContent, setModalContent] = useState<React.ReactNode>(null)

  // Lógica de reenvio de email (mantida aqui pois é compartilhada)
  async function handleResendConfirmation(email: string) {
    if (resendCooldown > 0) return
    setResendLoading(true)
    setResendError(null)
    setResendSuccess(false)
    try {
      const res = await fetch('/api/resend-confirmation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      })
      if (!res.ok) {
        const data = await res.json()
        setResendError(data.error || 'Erro ao reenviar e-mail.')
        setResendSuccess(false)
      } else {
        setResendSuccess(true)
        setResendCooldown(30)
        setResendError(null)
      }
    } catch {
      setResendError('Erro de rede.')
      setResendSuccess(false)
    } finally {
      setResendLoading(false)
      setModalContent(renderConfirmationModal(email))
      setModalOpen(true)
    }
  }

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  function renderConfirmationModal(email: string) {
    return (
      <div className="flex flex-col gap-4 items-center">
        <span className="text-green-400 text-lg font-semibold">Cadastro realizado!</span>
        <span className="text-zinc-300 text-center">
          Verifique seu e-mail para confirmar sua conta antes de fazer login.<br />
          <span className="text-zinc-500 text-sm">Caso não encontre o e-mail, verifique a caixa de spam.</span>
        </span>
        <button
          className="px-4 py-3 min-h-[48px] rounded bg-[#ffa300] text-black font-bold hover:bg-[#ff8c00] transition disabled:opacity-60"
          onClick={() => handleResendConfirmation(email)}
          disabled={resendLoading || resendCooldown > 0}
        >
          {resendLoading
            ? 'Reenviando...'
            : resendCooldown > 0
              ? `Reenviar e-mail em ${resendCooldown}s`
              : 'Reenviar e-mail de confirmação'}
        </button>
        {resendSuccess && <span className="text-green-300">E-mail reenviado com sucesso!</span>}
        {resendError && <span className="text-red-400">{resendError}</span>}
        <button
          className="mt-2 px-4 py-3 min-h-[48px] rounded bg-[#ffa300] text-black font-bold hover:bg-[#ff8c00] transition"
          onClick={() => { setTab('login'); setModalOpen(false); }}
        >
          Ir para login
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-black via-zinc-900 to-[#1a1a1a] px-4 sm:px-6 py-6 sm:py-8">
      <div className="w-full max-w-md mx-auto">
        {/* Logo */}
        <div className="text-center mb-6 sm:mb-8">
          <Link href="/">
            <Image src="/assets/images/logo.png" alt="Logo Vibe" width={200} height={70} className="mx-auto drop-shadow-lg w-40 sm:w-auto" priority />
          </Link>
        </div>

        {/* Card Principal */}
        <div className="bg-zinc-900/40 backdrop-blur-xl rounded-3xl border border-zinc-800/50 shadow-2xl overflow-hidden">
          {/* Tabs */}
          <div className="flex">
            <button
              onClick={() => setTab('login')}
              className={`flex-1 min-h-[48px] py-3 sm:py-4 flex items-center justify-center text-base sm:text-lg font-semibold transition-all duration-300 ${
                tab === 'login'
                  ? 'bg-gradient-to-r from-[#ffa300] to-yellow-400 text-black shadow-lg'
                  : 'text-zinc-400 hover:text-white bg-zinc-800/50'
              }`}
            >
              Entrar
            </button>
            <button
              onClick={() => setTab('register')}
              className={`flex-1 min-h-[48px] py-3 sm:py-4 flex items-center justify-center text-base sm:text-lg font-semibold transition-all duration-300 ${
                tab === 'register'
                  ? 'bg-gradient-to-r from-[#ffa300] to-yellow-400 text-black shadow-lg'
                  : 'text-zinc-400 hover:text-white bg-zinc-800/50'
              }`}
            >
              Cadastrar
            </button>
          </div>

          {/* Forms Container */}
          <div className="p-4 sm:p-8">
            {tab === 'login' ? (
              <LoginForm />
            ) : (
              <RegisterForm 
                onSuccess={(email) => {
                  setConfirmationEmail(email)
                  setConfirmationModalOpen(true)
                }}
                onResendNeeded={(email) => {
                  setConfirmationEmail(email)
                  setConfirmationModalOpen(true)
                }}
              />
            )}
          </div>
        </div>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        {modalContent}
      </Modal>

      <EmailConfirmationModal
        open={confirmationModalOpen}
        email={confirmationEmail}
        onClose={() => setConfirmationModalOpen(false)}
        onGoToLogin={() => { setTab('login'); setConfirmationModalOpen(false); }}
      />
    </div>
  )
}
